import {
  AppInsightsContext,
  type ReactPlugin,
} from "@microsoft/applicationinsights-react-js";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import Box, { type BoxProps } from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface FallBackUIProps extends BoxProps {
  reset: () => void;
  error?: Error | null;
}

function FallBackUI({ reset, error, ...rest }: FallBackUIProps) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:ComponentErrorBoundary",
  });
  return (
    <Box
      {...rest}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        width: "100%",
        height: "100%",
        ...rest.sx,
      }}
    >
      <Typography variant="h5" color="error.main">
        {t("errorMessage")}
      </Typography>
      {error?.message && (
        <Typography variant="body2" color="text.secondary">
          {error.message}
        </Typography>
      )}
      <Button onClick={reset} variant="outlined">
        {t("button")}
      </Button>
    </Box>
  );
}

// https://reactjs.org/docs/error-boundaries.html
export class ComponentErrorBoundary extends Component<
  { children?: ReactNode; fallBackBoxProps?: BoxProps },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children?: ReactNode; fallBackBoxProps?: BoxProps }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const appInsights = this.context as ReactPlugin;
    appInsights.trackException({
      error,
      exception: error,
      severityLevel: SeverityLevel.Error,
      properties: errorInfo,
    });
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  render() {
    const { hasError, error } = this.state;
    const { children, fallBackBoxProps } = this.props;
    if (hasError) {
      return (
        <FallBackUI
          {...fallBackBoxProps}
          error={error}
          reset={() => {
            this.setState({ hasError: false, error: null });
          }}
        />
      );
    }

    return children;
  }
}

ComponentErrorBoundary.contextType = AppInsightsContext;
